/**
 * Flow Logo Constants
 * Colors, sizes, and F-mark geometry derived from brand formulas.
 *
 * Essential Formulas (from logo-design skill):
 *   1. font_size  = icon_height × 0.8
 *   2. gap        = icon_height × 0.4
 *   3. baseline_y = icon_center_y + (font_size × 0.36)   [Inter cap-height/2]
 *   4. clear space: 1x = dot height (16px); icon 1x, lockup 1.5x
 *   5. min size:    lockup ≥ 120px, icon ≥ 20px
 *   6. backgrounds: monochrome only on colored backgrounds
 */

// ── F-mark geometry ──────────────────────────────────────────────────────────
export const FMARK = {
  /** Bar widths: top, middle, bottom (dot) */
  widths: [52, 34, 16] as const,
  /** Bar height (all three bars) */
  barH: 16,
  /** Vertical gap between bars */
  barGap: 6,
  /** Border radius (full pill) */
  rx: 8,
  /** Total icon width */
  get w() {
    return this.widths[0]; // 52
  },
  /** Total icon height: 3 bars + 2 gaps */
  get h() {
    return this.barH * 3 + this.barGap * 2; // 60
  },
  /** Clear space unit (1x = dot height) */
  get clearSpace() {
    return this.widths[2]; // 16
  },
} as const;

// ── Lockup proportions (derived from formulas) ──────────────────────────────
export const LOCKUP = {
  /** Icon-to-text gap = icon_height × 0.4 */
  gap: 60 * 0.4, // 24
  /** Font size = icon_height × 0.8 */
  fontSize: 60 * 0.8, // 48
  /** Font weight */
  fontWeight: 600,
  /** Letter spacing */
  letterSpacing: -1,
  /** Font family stack */
  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
  /** Padding around icon in lockup SVG */
  padding: 8,
  /** Lockup viewBox dimensions */
  viewBox: { w: 200, h: 76 },
} as const;

// ── Flow Coral color palette ─────────────────────────────────────────────────
export const FLOW_COLORS = {
  primary: '#bf5f60',
  secondary: '#924041',
  gradientStart: '#f08886',
  gradientEnd: '#a95050',
  dark: '#1a1a1a',
  light: '#ffffff',
  muted: 'rgba(191, 95, 96, 0.5)',
} as const;

// ── Size configurations ──────────────────────────────────────────────────────
export const HEADER_SIZES = {
  xs: { width: 64, height: 20 },
  sm: { width: 80, height: 24 },
  md: { width: 120, height: 36 },
  lg: { width: 160, height: 48 },
} as const;

export const SPINNER_SIZES = {
  sm: { width: 24, height: 24 },
  md: { width: 32, height: 32 },
  lg: { width: 48, height: 48 },
  xl: { width: 64, height: 64 },
} as const;

export const SPLASH_SIZES = {
  sm: { width: 160, height: 60 },
  md: { width: 240, height: 90 },
  lg: { width: 320, height: 120 },
  xl: { width: 400, height: 150 },
} as const;

export const HERO_SIZES = {
  sm: { width: 200, height: 100 },
  md: { width: 300, height: 150 },
  lg: { width: 400, height: 200 },
  xl: { width: 500, height: 250 },
} as const;

// ── Animation timing ─────────────────────────────────────────────────────────
export const ANIMATION_CONFIG = {
  easing: [0.4, 0, 0.2, 1] as const,
  durations: {
    hover: 0.3,
    spinner: 1.2,
    splash: 2.0,
    hero: 3.0,
  },
  speeds: {
    slow: 1.5,
    normal: 1.0,
    fast: 0.6,
  },
} as const;

// ── Color scheme presets ─────────────────────────────────────────────────────
export const COLOR_SCHEMES = {
  /** Default — inherits from parent text color */
  auto: 'currentColor',
  /** Solid white — for dark backgrounds */
  white: FLOW_COLORS.light,
  /** Translucent white — subtle on dark backgrounds */
  translucent: 'rgba(255, 255, 255, 0.7)',
  /** Brand coral — standalone on neutral backgrounds */
  brand: FLOW_COLORS.primary,
} as const;

export type ColorScheme = keyof typeof COLOR_SCHEMES;

// ── Theme helper ─────────────────────────────────────────────────────────────
export function getThemeColors(theme: 'dark' | 'light') {
  return {
    primary: theme === 'dark' ? FLOW_COLORS.gradientStart : FLOW_COLORS.primary,
    text: theme === 'dark' ? FLOW_COLORS.light : FLOW_COLORS.dark,
    background: 'transparent',
  };
}
